(() => {
  const text = (key, fallback) => window.RenlyUI?.[key] ?? fallback;
  const instances = new WeakMap();
  function initialize(root) {
    if (instances.has(root)) return instances.get(root);
    const slides = [...root.querySelectorAll('[data-slide]')];
    const thumbs = [...root.querySelectorAll('[data-gallery-to]')];
    const thumbRail = root.querySelector('.renly-gallery__thumbs');
    const galleryTrack = root.querySelector('[data-gallery-track]');
    const galleryStage = root.querySelector('.renly-gallery__stage');
    const galleryPrev = root.querySelector('[data-gallery-prev]');
    const galleryNext = root.querySelector('[data-gallery-next]');
    const reducedMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
    let currentSlide = 0;
    let gallery = null;
    let zoomed = false;
    function setZoom(next) {
      zoomed = next && matchMedia('(min-width: 769px)').matches;
      galleryStage?.classList.toggle('is-zoomed', zoomed);
      if (zoomed) galleryTrack?.focus({ preventScroll: true });
      if (gallery) gallery.allowTouchMove = !zoomed;
      galleryTrack?.setAttribute('aria-label', zoomed
        ? text('gallery_zoomed', 'Produktbild förstorad: tryck Escape eller Enter för att stänga')
        : text('gallery_zoom', 'Produktbilder: svep för fler bilder, tryck Enter för att förstora'));
    }
    function syncThumbnailRail() {
      if (!thumbRail || !galleryStage) return;
      thumbRail.style.maxHeight = getComputedStyle(thumbRail).flexDirection === 'column'
        ? `${Math.round(galleryStage.getBoundingClientRect().height)}px`
        : '';
    }
    function updateGalleryArrows() {
      if (galleryPrev) galleryPrev.hidden = currentSlide === 0 || slides.length < 2;
      if (galleryNext) galleryNext.hidden = currentSlide === slides.length - 1 || slides.length < 2;
    }
    function centerThumbnail(index) {
      const selected = thumbs[index];
      if (!thumbRail || !selected) return;
      const rail = thumbRail.getBoundingClientRect();
      const item = selected.getBoundingClientRect();
      const vertical = getComputedStyle(thumbRail).flexDirection === 'column';
      const delta = vertical
        ? item.top + item.height / 2 - rail.top - rail.height / 2
        : item.left + item.width / 2 - rail.left - rail.width / 2;
      thumbRail.scrollTo({
        [vertical ? 'top' : 'left']: Math.max(0, (vertical ? thumbRail.scrollTop : thumbRail.scrollLeft) + delta),
        behavior: reducedMotion() ? 'instant' : 'smooth'
      });
    }
    function selectSlide(index) {
      if (zoomed) setZoom(false);
      currentSlide = index;
      updateGalleryArrows();
      slides.forEach((slide, i) => slide.classList.toggle('is-active', i === index));
      thumbs.forEach((thumb, i) => {
        thumb.classList.toggle('is-active', i === index);
        thumb.setAttribute('aria-pressed', i === index ? 'true' : 'false');
      });
      centerThumbnail(index);
    }
    gallery = galleryStage && typeof window.Swiper === 'function'
      ? new window.Swiper(galleryStage, {
          speed: reducedMotion() ? 0 : 480,
          slidesPerView: 1,
          loop: slides.length > 1,
          spaceBetween: 0,
          simulateTouch: true,
          grabCursor: true,
          threshold: 4,
          resistanceRatio: 0.35,
          on: { realIndexChange(swiper) { selectSlide(swiper.realIndex); } }
        })
      : null;
    thumbs.forEach((thumb, index) => thumb.addEventListener('click', () => {
      if (!gallery) return;
      gallery.slideToLoop(index, reducedMotion() ? 0 : 480);
      centerThumbnail(index);
    }));
    galleryPrev?.addEventListener('click', () => gallery?.slidePrev(reducedMotion() ? 0 : 480));
    galleryNext?.addEventListener('click', () => gallery?.slideNext(reducedMotion() ? 0 : 480));
    window.addEventListener('resize', updateGalleryArrows);
    window.addEventListener('resize', syncThumbnailRail);
    if ('ResizeObserver' in window && galleryStage) new ResizeObserver(syncThumbnailRail).observe(galleryStage);
    selectSlide(0);
    syncThumbnailRail();
    galleryTrack?.addEventListener('keydown', event => {
      if (event.key === 'Enter' || event.key === ' ') {
        if (!matchMedia('(min-width: 769px)').matches) return;
        event.preventDefault();
        setZoom(!zoomed);
        return;
      }
      if (event.key === 'Escape' && zoomed) { event.preventDefault(); setZoom(false); return; }
      if (!gallery || (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight')) return;
      event.preventDefault();
      if (event.key === 'ArrowRight') gallery.slideNext(reducedMotion() ? 0 : 480);
      else gallery.slidePrev(reducedMotion() ? 0 : 480);
    });
    galleryStage?.addEventListener('click', event => {
      if (!event.target.closest('.renly-gallery__image') || (gallery && !gallery.allowClick)) return;
      const bounds = galleryStage.getBoundingClientRect();
      galleryStage.style.setProperty('--zoom-x', `${Math.max(0, Math.min(100, (event.clientX - bounds.left) / bounds.width * 100))}%`);
      galleryStage.style.setProperty('--zoom-y', `${Math.max(0, Math.min(100, (event.clientY - bounds.top) / bounds.height * 100))}%`);
      setZoom(!zoomed);
    });
    galleryStage?.addEventListener('pointermove', event => {
      if (!zoomed) return;
      const bounds = galleryStage.getBoundingClientRect();
      galleryStage.style.setProperty('--zoom-x', `${Math.max(0, Math.min(100, (event.clientX - bounds.left) / bounds.width * 100))}%`);
      galleryStage.style.setProperty('--zoom-y', `${Math.max(0, Math.min(100, (event.clientY - bounds.top) / bounds.height * 100))}%`);
    });
    window.addEventListener('resize', () => { if (zoomed && !matchMedia('(min-width: 769px)').matches) setZoom(false); });

    const instance = { slides, gallery, reducedMotion, centerThumbnail };
    instances.set(root, instance);
    return instance;
  }
  window.RenlyGallery = { initialize };
  function boot() { document.querySelectorAll('[data-gallery]').forEach(initialize); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true }); else boot();
  document.addEventListener('shopify:section:load', boot);
})();
